// src/components/multiplayer/JoinGamePanel.tsx
import React, { useState, useEffect, useCallback } from "react";
import "./JoinGamePanel.css";

interface AvailableGame {
  roomId: string;
  host: string;
  hostUserId?: string;
  boardSize: number;
  gameName?: string;
  variantId?: string;
  presetId?: string;
  createdAt: string;
  status: "waiting" | "ready";
  opponentUsername?: string;
}

// Game preset mapping for display
const presetNames: Record<string, string> = {
  "0808-001": "Traditional FIDE Chess (8×8)",
  "0909-001": "Perfect Chess (9×9)",
  "1010-001": "Breakout Chess (10×10)",
  "1111-001": "Next Level Chess (11×11)",
  "1212-001": "Arrival Chess (12×12)",
};

interface JoinGamePanelProps {
  onJoinGame: (roomId: string) => void;
  onSelectOwnGame?: (game: AvailableGame) => void;
  currentUserId?: string;
  serverUrl?: string;
}

export const JoinGamePanel: React.FC<JoinGamePanelProps> = ({
  onJoinGame,
  onSelectOwnGame,
  currentUserId,
  serverUrl = "",
}) => {
  const [games, setGames] = useState<AvailableGame[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Fetch available games from API
  const fetchAvailableGames = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await fetch(`${serverUrl}/api/games/available`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch games: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Filter to only show "waiting" games (not "ready" since those have opponent)
      const waitingGames = data.filter((game: AvailableGame) => game.status === "waiting");
      
      setGames(waitingGames);
    } catch (err) {
      console.error("Error fetching available games:", err);
      setError(err instanceof Error ? err.message : "Failed to load games");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [serverUrl]);

  // Initial fetch on mount
  useEffect(() => {
    fetchAvailableGames();
  }, [fetchAvailableGames]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAvailableGames(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchAvailableGames]);

  // Manual refresh handler
  const handleRefresh = () => {
    fetchAvailableGames(true);
  };

  // Check if game is owned by the current user
  const isOwnGame = (game: AvailableGame): boolean => {
    return currentUserId ? game.hostUserId === currentUserId : false;
  };

  // Handle game click - either join or select own game
  const handleGameClick = (game: AvailableGame) => {
    if (isOwnGame(game) && onSelectOwnGame) {
      onSelectOwnGame(game);
    } else {
      onJoinGame(game.roomId);
    }
  };

  // Format date/time for display
  const formatCreatedAt = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  // Get variant display name from presetId
  const getVariantName = (presetId?: string): string => {
    if (!presetId) return "Standard";
    return presetNames[presetId] || "Standard";
  };

  return (
    <div className="join-game-panel-container">
      {/* Header with Refresh Button */}
      <div className="join-game-header">
        <div className="game-count">
          {!loading && (
            <span className="count-badge">
              {games.length} {games.length === 1 ? "game" : "games"}
            </span>
          )}
        </div>
        <button
          className={`refresh-btn ${refreshing ? "refreshing" : ""}`}
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh game list"
        >
          <span className="refresh-icon">🔄</span>
          <span className="refresh-text">Refresh</span>
        </button>
      </div>

      {/* Game List Content */}
      <div className="join-game-content">
        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading available games...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-state">
            <span className="error-icon">⚠️</span>
            <p className="error-message">{error}</p>
            <button className="retry-btn" onClick={handleRefresh}>
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && games.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">🎮</span>
            <h3 className="empty-title">No Games Available</h3>
            <p className="empty-message">
              Be the first to create a game!
            </p>
            <p className="empty-hint">
              Use the <strong>Create Game</strong> panel below to start a new game
            </p>
          </div>
        )}

        {/* Game Cards List */}
        {!loading && !error && games.length > 0 && (
          <div className="game-cards-list">
            {games.map((game) => (
              <div key={game.roomId} className="game-card">
                <div className="game-card-header">
                  <div className="room-code">
                    <span className="room-code-label">Room:</span>
                    <span className="room-code-value">{game.roomId}</span>
                  </div>
                  <span className="game-time">{formatCreatedAt(game.createdAt)}</span>
                </div>

                <div className="game-card-body">
                  <div className="game-info-row">
                    <span className="info-icon">👤</span>
                    <span className="info-label">Host:</span>
                    <span className="info-value">{game.host}</span>
                  </div>

                  <div className="game-info-row">
                    <span className="info-icon">📐</span>
                    <span className="info-label">Board:</span>
                    <span className="info-value">{game.boardSize}×{game.boardSize}</span>
                  </div>

                  <div className="game-info-row">
                    <span className="info-icon">🎨</span>
                    <span className="info-label">Variant:</span>
                    <span className="info-value">{getVariantName(game.presetId)}</span>
                  </div>

                  {game.gameName && (
                    <div className="game-info-row game-name">
                      <span className="info-icon">🏷️</span>
                      <span className="info-label">Name:</span>
                      <span className="info-value">{game.gameName}</span>
                    </div>
                  )}
                </div>

                <div className="game-card-footer">
                  <button
                    className={isOwnGame(game) ? "manage-btn" : "join-btn"}
                    onClick={() => handleGameClick(game)}
                  >
                    {isOwnGame(game) ? (
                      <>
                        <span className="join-btn-icon">🏠</span>
                        <span className="join-btn-text">Manage</span>
                      </>
                    ) : (
                      <>
                        <span className="join-btn-icon">▶️</span>
                        <span className="join-btn-text">Join Game</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinGamePanel;
